/**
 * useOrgAwareCatchball
 *
 * Bridge hook that takes the current user's org node and a loop, and returns
 * everything Catchball needs to know about this user's relationship to that loop —
 * their tier, what they can see, whether they're obligated to act.
 *
 * Two setup modes (see CatchballVirtualConfig):
 *   'org_chart' (default) — tiers derived from cycle.orgScopeNodeId + org hierarchy.
 *   'virtual'             — tiers derived from explicitly selected users/groups stored
 *                           in catchball_virtual_configs (migration 378).
 *
 * When loop.orgScopeNodeId is absent AND no virtual config exists, falls back to
 * legacy behavior (canSeeLoop: true, userTier: null).
 */

import { useMemo } from 'react';
import { useEnterpriseIdentityStore } from '../../stores/prioritization/enterpriseIdentityStore';
import { useDegapProcessStore } from '../../stores/prioritization/degapProcessStore';
import { useCatchballVirtualConfigStore } from '../../stores/prioritization/catchballVirtualConfigStore';
import { useBallsForLoop } from '../../stores/prioritization/catchballBallStore';
import { useCatchballTierTurnStore } from '../../stores/prioritization/catchballTierTurnStore';
import type { CatchballLoop, BallPossession, CatchballLevelConfig } from '../../types/prioritization/catchballLoop';
import { deriveTierFromOrgDepth, getBallPossession, normalizeLoopState } from '../../types/prioritization/catchballLoop';
import type { ActiveBallPosition } from '../../types/prioritization/catchballTierTurn';
import type { OrgUnit } from '../../types/prioritization/enterpriseIdentity';
import type { CatchballVirtualConfig } from '../../types/prioritization/catchballVirtualConfig';
import { isUserDirectlyListed } from '../../types/prioritization/catchballVirtualConfig';

/** Stable empty array so the active-positions selector returns a referentially
 *  stable value for loops with no turns (avoids zustand re-render loops). */
const EMPTY_ACTIVE_POSITIONS: ActiveBallPosition[] = [];

/** Minimal ball shape required for recipient-based visibility checks. */
export interface BallRecipientSlice {
  recipientUserId: string | null;
  ballStatus: string;
}

export interface OrgAwareCatchballResult {
  /** This user's tier for this loop. Null if org scope not configured. */
  userTier: BallPossession | null;

  /**
   * True if this user personally holds the ball right now.
   * Kept for back-compat; equals `iAmHolder` (it used to mean "the ball is with
   * my tier", but possession is now per-person, not per-tier).
   */
  ballIsWithMe: boolean;

  /** The user currently holding the ball, or null when unclaimed. */
  currentHolderUserId: string | null;

  /** True if this user personally holds the ball. */
  iAmHolder: boolean;

  /** True if nobody holds the ball and the loop is still live (not closed). */
  ballUnclaimed: boolean;

  /**
   * True if this user may claim ("grab") the unclaimed ball.
   * Client-side approximation for UX — the claim RPC is authoritative.
   */
  canClaimBall: boolean;

  /** True if this user can see this loop at all */
  canSeeLoop: boolean;

  /** True if this user can see content from tiers below them */
  canSeeBelow: boolean;

  /** True if this user is allowed to act on the loop right now (holder-only) */
  canActOnLoop: boolean;

  /** True if this user is in the current adjacent activity lane for this loop stage */
  canContributeActivity: boolean;

  /** The org unit node the loop is anchored to, if any */
  rootNode: OrgUnit | null;

  /** This user's org unit node, if found */
  userNode: OrgUnit | null;

  /** Human-readable tier label for this user */
  tierLabel: string | null;

  /** Human-readable label for the org nodes currently receiving the ball */
  recipientLabel: string | null;

  /** Human-readable label for the org nodes that currently hold decision authority */
  currentHolderLabel: string | null;

  /** Human-readable label for the adjacent org nodes collaborating in the activity lane */
  activityPartnerLabel: string | null;

  /** Human-readable label for the next explicit handoff target from the current state */
  nextHandoffLabel: string | null;

  // ── Turns-first position (ADR-catchball-consolidation) ──────────────────────
  // Populated when a catchball_level_turn exists for the loop (authority position
  // comes from catchball_active_branch_positions). Absent/null for legacy
  // loops with no turns yet, which fall back to the three-tier ternary. Additive
  // and optional so existing consumers never break.

  /** Raw active ball position from the turns view, when turns exist for this loop. */
  activePosition?: ActiveBallPosition | null;

  /** Org unit that holds the ball this turn. Possession is org-unit-keyed under turns. */
  holderOrgUnitId?: string | null;

  /** Absolute org depth of the holder unit. */
  holderDepth?: number | null;

  /** 0-based depth of the holder relative to the loop root (arbitrary N tiers). */
  holderTierIndex?: number | null;

  /** Display-only tier bucket of the holder depth (façade over depth; not authority). */
  holderTier?: BallPossession | null;

  /** True when this user's org placement is the active turn's holder stop. */
  atHolderStop?: boolean;

  /**
   * Ball has moved to another stop and is waiting for someone there to Take it.
   * True for viewers who are not the taker at that stop (e.g. the passer).
   */
  ballAwaitingTake?: boolean;
}

export type LoopAccessDetails = {
  userTier: BallPossession | null;
  ballIsWithMe: boolean;
  currentHolderUserId: string | null;
  iAmHolder: boolean;
  ballUnclaimed: boolean;
  canClaimBall: boolean;
  canSeeLoop: boolean;
  canSeeBelow: boolean;
  canActOnLoop: boolean;
  canContributeActivity: boolean;
  rootNode: OrgUnit | null;
  userNode: OrgUnit | null;
  tierLabel: string | null;
  recipientLabel: string | null;
  currentHolderLabel: string | null;
  activityPartnerLabel: string | null;
  nextHandoffLabel: string | null;
  // Turns-first position (see OrgAwareCatchballResult for docs). Optional/additive.
  activePosition?: ActiveBallPosition | null;
  holderOrgUnitId?: string | null;
  holderDepth?: number | null;
  holderTierIndex?: number | null;
  holderTier?: BallPossession | null;
  atHolderStop?: boolean;
  ballAwaitingTake?: boolean;
};

function buildRecipientLabel(loop: CatchballLoop, orgUnitsMap: Map<string, OrgUnit>): string | null {
  const recipientIds = loop.intent?.recipientOrgUnitIds ?? [];
  if (recipientIds.length === 0) return null;

  const names = recipientIds.map((id) => orgUnitsMap.get(id)?.displayName ?? id);
  if (names.length === 1) return names[0];
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  return `${names.slice(0, -1).join(', ')}, and ${names[names.length - 1]}`;
}

function buildNodeLabel(nodes: OrgUnit[]): string | null {
  if (nodes.length === 0) return null;
  const names = [...new Set(nodes.map((node) => node.displayName))];
  if (names.length === 1) return names[0];
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  return `${names.slice(0, -1).join(', ')}, and ${names[names.length - 1]}`;
}

function getDirectChildren(allUnits: OrgUnit[], parentIds: string[]): OrgUnit[] {
  if (parentIds.length === 0) return [];
  return allUnits
    .filter((unit) => unit.parentId && parentIds.includes(unit.parentId))
    .sort((left, right) => left.displayName.localeCompare(right.displayName));
}

function getUnitsByIds(allUnits: OrgUnit[], ids: string[]): OrgUnit[] {
  if (ids.length === 0) return [];
  const byId = new Map(allUnits.map((unit) => [unit.id, unit]));
  return ids
    .map((id) => byId.get(id) ?? null)
    .filter((unit): unit is OrgUnit => unit !== null);
}

function isDirectlyPlacedAtNode(userNode: OrgUnit, targetNodeIds: string[]): boolean {
  if (targetNodeIds.length === 0) return false;
  return targetNodeIds.includes(userNode.id);
}

/** True if the user's org node is the holder or any descendant org unit under a holder node. */
function isUserUnderAnyHolderNode(
  userNode: OrgUnit,
  holderIds: string[],
  orgUnitsMap: Map<string, OrgUnit>,
): boolean {
  if (holderIds.length === 0) return false;
  let cursor: OrgUnit | null = userNode;
  const seen = new Set<string>();
  while (cursor && !seen.has(cursor.id)) {
    seen.add(cursor.id);
    if (holderIds.includes(cursor.id)) return true;
    cursor = cursor.parentId ? orgUnitsMap.get(cursor.parentId) ?? null : null;
  }
  return false;
}

// ── Ball possession helpers ────────────────────────────────────────────────

/**
 * Per-person ball possession facts, shared by every access path.
 * Possession lives on `catchball_loops.ball_holder_user_id` (written only by
 * the claim/release/transition RPCs); this is the client-side mirror.
 */
function computePossessionFacts(
  loop: CatchballLoop | null,
  currentUserId: string | null,
): { currentHolderUserId: string | null; iAmHolder: boolean; ballUnclaimed: boolean } {
  const currentHolderUserId = loop?.ballHolderUserId ?? null;
  const iAmHolder =
    currentHolderUserId !== null && currentUserId !== null && currentHolderUserId === currentUserId;
  // Terminal loops (closed/DECLINED) have no claimable ball.
  const ballUnclaimed =
    loop !== null && !currentHolderUserId && normalizeLoopState(loop.state) !== 'DECLINED';
  return { currentHolderUserId, iAmHolder, ballUnclaimed };
}

/**
 * Org-chart-mode claim eligibility, refined by the loop's levelConfig for the
 * tier that currently owns the ball (Designer per-node assignments):
 *   - a participant with a non-empty userId → only that user is eligible via
 *     that entry (no org-subtree broadening);
 *   - a participant with a groupId → group members are eligible. Group
 *     membership is not synchronously resolvable client-side unless the caller
 *     provides `currentUserGroupIds`; when absent we stay optimistic and let
 *     the claim RPC reject (server is exact);
 *   - a participant with only an orgUnitId, or an empty tier config
 *     (pre-Designer loops) → the existing placement/subtree logic
 *     (`placementEligible`) applies.
 */
function computeOrgChartClaimEligibility(
  levelConfig: CatchballLevelConfig | undefined,
  tier: BallPossession,
  currentUserId: string,
  placementEligible: boolean,
  currentUserGroupIds?: ReadonlySet<string> | null,
): boolean {
  const participants = (levelConfig?.[tier] ?? []).filter(
    (p) => (p.userId && p.userId.trim().length > 0) || p.groupId || p.orgUnitId,
  );
  if (participants.length === 0) return placementEligible;

  for (const participant of participants) {
    if (participant.userId && participant.userId.trim().length > 0) {
      if (participant.userId === currentUserId) return true;
      continue; // explicit pick — does not broaden to the org unit
    }
    if (participant.groupId) {
      if (!currentUserGroupIds) return true; // membership unknown client-side → optimistic
      if (currentUserGroupIds.has(participant.groupId)) return true;
      continue;
    }
    // Org-unit-wide participant — fall back to placement under the holder node(s).
    if (placementEligible) return true;
  }
  return false;
}

// ── Virtual mode helpers ───────────────────────────────────────────────────

/**
 * Pure-function access computation for virtual participant mode.
 * Replaces org-chart hierarchy traversal with explicit participant list checks.
 *
 * Tier mapping:
 *   executive — cycle owner, loop creator, or loop request issuer
 *   middle    — ball_recipients from the virtual config
 *   bottom    — default_participants from the virtual config
 */
function computeVirtualModeDetails(
  loop: CatchballLoop,
  currentUserId: string,
  virtualConfig: CatchballVirtualConfig,
  cycleOwnerId?: string | null,
): LoopAccessDetails {
  const { currentHolderUserId, iAmHolder, ballUnclaimed } = computePossessionFacts(
    loop,
    currentUserId,
  );

  const nullResult: LoopAccessDetails = {
    userTier: null,
    ballIsWithMe: false,
    currentHolderUserId,
    iAmHolder: false,
    ballUnclaimed,
    canClaimBall: false,
    canSeeLoop: false,
    canSeeBelow: false,
    canActOnLoop: false,
    canContributeActivity: false,
    rootNode: null,
    userNode: null,
    tierLabel: null,
    recipientLabel: null,
    currentHolderLabel: null,
    activityPartnerLabel: null,
    nextHandoffLabel: null,
  };

  const { ballRecipients, defaultParticipants } = virtualConfig;

  const isExecutive =
    loop.createdBy === currentUserId ||
    loop.requestIssuedBy === currentUserId ||
    (cycleOwnerId != null && cycleOwnerId === currentUserId);
  const isMiddle = isUserDirectlyListed(currentUserId, ballRecipients);
  const isBottom = isUserDirectlyListed(currentUserId, defaultParticipants);

  const canSeeLoop = isExecutive || isMiddle || isBottom;
  if (!canSeeLoop) return nullResult;

  const userTier: BallPossession = isExecutive ? 'executive' : isMiddle ? 'middle' : 'bottom';
  const currentPossession = getBallPossession(loop.state);

  // Hand-off actions are holder-only; the old tier-possession check now feeds
  // claim eligibility instead.
  const ballIsWithMe = iAmHolder;
  const canActOnLoop = iAmHolder;

  // Claim eligibility at the tier that currently owns the ball. Group refs are
  // not expanded client-side (isUserDirectlyListed is user-refs only), so any
  // group ref keeps the claim optimistic — the RPC's membership check is exact.
  const possessionTierRefs =
    currentPossession === 'middle'
      ? ballRecipients
      : currentPossession === 'bottom'
      ? defaultParticipants
      : null;
  const claimEligible =
    possessionTierRefs === null
      ? isExecutive
      : isUserDirectlyListed(currentUserId, possessionTierRefs) ||
        possessionTierRefs.some((ref) => ref.type === 'group');
  const canClaimBall = ballUnclaimed && claimEligible;

  // Activity lanes stay open under the pre-possession rules (S1–S5 screens are
  // not holder-gated): the tier with the ball plus the adjacent tier.
  const canContributeActivity =
    iAmHolder ||
    currentPossession === userTier ||
    (currentPossession === 'middle' && userTier === 'bottom') ||
    (currentPossession === 'bottom' && userTier === 'middle');

  const recipientLabel =
    ballRecipients.length === 0
      ? null
      : ballRecipients.length === 1
      ? ballRecipients[0].displayName
      : ballRecipients.length === 2
      ? `${ballRecipients[0].displayName} & ${ballRecipients[1].displayName}`
      : `${ballRecipients.slice(0, 2).map((r) => r.displayName).join(', ')} +${ballRecipients.length - 2}`;

  const currentHolderLabel =
    currentPossession === 'executive'
      ? null // cycle owner — no stored label
      : currentPossession === 'middle'
      ? recipientLabel
      : defaultParticipants.length === 0
      ? 'Default participants'
      : defaultParticipants.length === 1
      ? defaultParticipants[0].displayName
      : `${defaultParticipants.slice(0, 2).map((r) => r.displayName).join(', ')}${defaultParticipants.length > 2 ? ` +${defaultParticipants.length - 2}` : ''}`;

  const tierLabels: Record<BallPossession, string> = {
    executive: 'Executive',
    middle: 'Ball recipients',
    bottom: 'Default participants',
  };

  return {
    userTier,
    ballIsWithMe,
    currentHolderUserId,
    iAmHolder,
    ballUnclaimed,
    canClaimBall,
    canSeeLoop: true,
    canSeeBelow: isExecutive || isMiddle,
    canActOnLoop,
    canContributeActivity,
    rootNode: null,
    userNode: null,
    tierLabel: tierLabels[userTier],
    recipientLabel,
    currentHolderLabel,
    activityPartnerLabel:
      currentPossession === 'middle' ? currentHolderLabel : recipientLabel,
    nextHandoffLabel:
      currentPossession === 'executive'
        ? recipientLabel
        : currentPossession === 'middle'
        ? null
        : recipientLabel,
  };
}

// ── Main computation ───────────────────────────────────────────────────────

/**
 * True when the current user has an active (non-draft, non-closed) ball
 * explicitly addressed to them in this loop.
 *
 * This is the "explicit recipient" fast-path: regardless of org-chart
 * placement or virtual-config membership, if a ball was sent to you by UUID
 * you must be able to see and act on the loop.
 */
function isExplicitBallRecipient(
  currentUserId: string,
  ballsForLoop: BallRecipientSlice[],
): boolean {
  const ACTIVE_STATUSES = new Set([
    'sent', 'opened', 'gathering_input', 'in_local_deliberation',
    'proposal_drafted', 'proposal_returned', 'under_review',
    'returned_for_revision',
  ]);
  return ballsForLoop.some(
    (b) => b.recipientUserId === currentUserId && ACTIVE_STATUSES.has(b.ballStatus),
  );
}

export function computeOrgAwareDetails(
  loop: CatchballLoop | null,
  currentUserId: string | null,
  orgUnitsMap: Map<string, OrgUnit>,
  userNodeIndex: Map<string, string>,
  fallbackOrgScopeNodeId?: string | null,
  virtualConfig?: CatchballVirtualConfig | null,
  ballsForLoop?: BallRecipientSlice[],
  cycleOwnerId?: string | null,
  currentUserGroupIds?: ReadonlySet<string> | null,
): LoopAccessDetails {
  const { currentHolderUserId, iAmHolder, ballUnclaimed } = computePossessionFacts(
    loop,
    currentUserId,
  );

  const nullResult: LoopAccessDetails = {
    userTier: null,
    ballIsWithMe: iAmHolder,
    currentHolderUserId,
    iAmHolder,
    ballUnclaimed,
    canClaimBall: false,
    canSeeLoop: true,
    canSeeBelow: true,
    canActOnLoop: iAmHolder,
    canContributeActivity: false,
    rootNode: null,
    userNode: null,
    tierLabel: null,
    recipientLabel: null,
    currentHolderLabel: null,
    activityPartnerLabel: null,
    nextHandoffLabel: null,
  };

  if (!loop) return { ...nullResult, canActOnLoop: false };
  if (!currentUserId) return { ...nullResult, canActOnLoop: false };

  // Virtual mode: bypass org-chart logic entirely
  if (virtualConfig?.setupMode === 'virtual') {
    return computeVirtualModeDetails(loop, currentUserId, virtualConfig, cycleOwnerId);
  }

  const effectiveOrgScopeNodeId = loop.orgScopeNodeId ?? fallbackOrgScopeNodeId ?? null;
  const recipientLabel = buildRecipientLabel(loop, orgUnitsMap);

  const isBallRecipient =
    currentUserId != null &&
    isExplicitBallRecipient(currentUserId, ballsForLoop ?? []);

  if (!effectiveOrgScopeNodeId) {
    // No org scope — restrict to users directly involved in the loop or
    // anyone who was explicitly named as a ball recipient.
    const isLoopParticipant =
      loop.createdBy === currentUserId ||
      loop.ballHolderUserId === currentUserId ||
      loop.requestIssuedBy === currentUserId ||
      isBallRecipient;
    return {
      ...nullResult,
      userTier: isBallRecipient && !isLoopParticipant ? 'middle' : null,
      canSeeLoop: isLoopParticipant,
      canActOnLoop: iAmHolder,
      canClaimBall: ballUnclaimed && isLoopParticipant,
      canContributeActivity: isLoopParticipant,
      recipientLabel,
      currentHolderLabel: recipientLabel,
      nextHandoffLabel: null,
    };
  }

  const allUnits = Array.from(orgUnitsMap.values());
  const rootNode = allUnits.find((u) => u.id === effectiveOrgScopeNodeId) ?? null;
  if (!rootNode) {
    const isLoopParticipant =
      loop.createdBy === currentUserId ||
      loop.ballHolderUserId === currentUserId ||
      loop.requestIssuedBy === currentUserId ||
      isBallRecipient;
    return {
      ...nullResult,
      userTier: isBallRecipient && !isLoopParticipant ? 'middle' : null,
      canSeeLoop: isLoopParticipant,
      canActOnLoop: iAmHolder,
      canClaimBall: ballUnclaimed && isLoopParticipant,
      canContributeActivity: isLoopParticipant,
      recipientLabel,
      currentHolderLabel: recipientLabel,
      nextHandoffLabel: null,
    };
  }

  const orgUnitId = userNodeIndex.get(currentUserId);
  const userNode = orgUnitId ? orgUnitsMap.get(orgUnitId) ?? null : null;
  if (!userNode) {
    // User has no org unit assignment — fall back to direct participation check.
    // This covers loop creators, ball holders, request issuers, and anyone who
    // was explicitly named as a ball recipient (recipient_user_id on catchball_balls).
    const isLoopParticipant =
      loop.createdBy === currentUserId ||
      loop.ballHolderUserId === currentUserId ||
      loop.requestIssuedBy === currentUserId ||
      isBallRecipient;
    return {
      ...nullResult,
      rootNode,
      userTier: isBallRecipient ? 'middle' : null,
      canSeeLoop: isLoopParticipant,
      canActOnLoop: iAmHolder,
      canClaimBall: ballUnclaimed && isLoopParticipant,
      canContributeActivity: isLoopParticipant,
      canSeeBelow: isLoopParticipant,
      recipientLabel,
      currentHolderLabel: recipientLabel,
      nextHandoffLabel: null,
    };
  }

  // Primary check: materialized path string (fast).
  // Fallback: walk parentId chain — handles org units where path is not yet set.
  const inSubtreeByPath =
    userNode.path?.includes(rootNode.id) === true || userNode.id === rootNode.id;
  const inSubtreeByParent = !inSubtreeByPath && (() => {
    let cursor: OrgUnit | null = userNode;
    const visited = new Set<string>();
    while (cursor && !visited.has(cursor.id)) {
      visited.add(cursor.id);
      if (cursor.id === rootNode.id) return true;
      cursor = cursor.parentId ? (orgUnitsMap.get(cursor.parentId) ?? null) : null;
    }
    return false;
  })();
  const canSeeLoop = inSubtreeByPath || inSubtreeByParent || isBallRecipient;
  if (!canSeeLoop) {
    return {
      ...nullResult,
      rootNode,
      userNode,
      canSeeLoop: false,
      canSeeBelow: false,
      canActOnLoop: iAmHolder,
      canContributeActivity: false,
      recipientLabel,
      currentHolderLabel: recipientLabel,
      nextHandoffLabel: recipientLabel,
    };
  }

  // If the only reason they can see the loop is that a ball was explicitly sent
  // to them, treat them as 'middle' tier — eligible to claim, holder-gated to act.
  if (isBallRecipient && !inSubtreeByPath && !inSubtreeByParent) {
    return {
      ...nullResult,
      rootNode,
      userNode,
      userTier: 'middle',
      ballIsWithMe: iAmHolder,
      canSeeLoop: true,
      canSeeBelow: false,
      canActOnLoop: iAmHolder,
      canClaimBall: ballUnclaimed,
      canContributeActivity: true,
      tierLabel: 'Ball recipient',
      recipientLabel,
      currentHolderLabel: recipientLabel,
      nextHandoffLabel: null,
    };
  }

  const subtreeNodes = allUnits.filter((u) => u.path?.includes(rootNode.id));
  const maxDepth = subtreeNodes.reduce((max, u) => Math.max(max, u.depth), rootNode.depth);
  const userTier = deriveTierFromOrgDepth(userNode.depth, rootNode.depth, maxDepth);
  const currentPossession = getBallPossession(loop.state);
  const levelZeroNodes = [rootNode];
  const levelOneNodes = getUnitsByIds(allUnits, loop.intent?.recipientOrgUnitIds ?? []);
  const resolvedLevelOneNodes =
    levelOneNodes.length > 0 ? levelOneNodes : getDirectChildren(allUnits, [rootNode.id]);
  const levelTwoNodes = getDirectChildren(allUnits, resolvedLevelOneNodes.map((node) => node.id));
  const levelThreeNodes = getDirectChildren(allUnits, levelTwoNodes.map((node) => node.id));

  let currentHolderNodes: OrgUnit[] = levelZeroNodes;
  let activityPartnerNodes: OrgUnit[] = resolvedLevelOneNodes;
  let nextHandoffNodes: OrgUnit[] = resolvedLevelOneNodes;

  switch (loop.state) {
    case 'drafting':
    case 'executive_review':
    case 'output_ready':
    case 'closed':
    case 'PROPOSED':
    case 'FINAL_REJECTED':
    case 'DECLINED':
    case 'proposed':
      currentHolderNodes = levelZeroNodes;
      activityPartnerNodes = resolvedLevelOneNodes;
      nextHandoffNodes = resolvedLevelOneNodes;
      break;
    case 'tossed_to_middle':
    case 'middle_working':
    case 'OPEN':
    case 'REVISION_REQUESTED':
    case 'ACCEPTED':
      currentHolderNodes = resolvedLevelOneNodes;
      activityPartnerNodes = levelTwoNodes;
      nextHandoffNodes = levelTwoNodes;
      break;
    case 'returned_to_middle':
      currentHolderNodes = resolvedLevelOneNodes;
      activityPartnerNodes = levelTwoNodes;
      nextHandoffNodes = levelZeroNodes;
      break;
    case 'tossed_to_bottom':
    case 'bottom_working':
    case 'accepted_provisional':
    case 'returned':
    case 'rejected':
      currentHolderNodes = levelTwoNodes.length > 0 ? levelTwoNodes : resolvedLevelOneNodes;
      activityPartnerNodes = levelThreeNodes;
      nextHandoffNodes = resolvedLevelOneNodes;
      break;
    case 'returned_to_top':
      currentHolderNodes = levelZeroNodes;
      activityPartnerNodes = resolvedLevelOneNodes;
      nextHandoffNodes = levelZeroNodes;
      break;
    default:
      currentHolderNodes = levelZeroNodes;
      activityPartnerNodes = resolvedLevelOneNodes;
      nextHandoffNodes = resolvedLevelOneNodes;
      break;
  }

  const currentHolderNodeIds = currentHolderNodes.map((node) => node.id);
  const activityPartnerNodeIds = activityPartnerNodes.map((node) => node.id);
  const isCurrentHolder = isDirectlyPlacedAtNode(userNode, currentHolderNodeIds);
  const isActivityPartner = isDirectlyPlacedAtNode(userNode, activityPartnerNodeIds);
  // Middle/bottom: anyone in the holder org subtree is placement-eligible
  // (e.g. trainers under the recipient team).
  // Executive: only users placed on the executive holder node(s), not the whole subtree.
  const inHolderOrgSubtree =
    (currentPossession === 'middle' || currentPossession === 'bottom') &&
    isUserUnderAnyHolderNode(userNode, currentHolderNodeIds, orgUnitsMap);
  // The old tier/subtree check no longer grants action rights — it is the
  // placement basis for claim eligibility instead. Acting is holder-only.
  const placementEligible =
    currentPossession === 'middle' || currentPossession === 'bottom'
      ? inHolderOrgSubtree
      : isCurrentHolder;
  const canActOnLoop = iAmHolder;
  const ballIsWithMe = iAmHolder;
  const canClaimBall =
    ballUnclaimed &&
    computeOrgChartClaimEligibility(
      loop.levelConfig,
      currentPossession,
      currentUserId,
      placementEligible,
      currentUserGroupIds,
    );

  // Activity lanes stay open under the pre-possession rules (S1–S5 screens are
  // not holder-gated).
  const canContributeActivity = iAmHolder || placementEligible || isActivityPartner;
  const currentHolderLabel = buildNodeLabel(currentHolderNodes) ?? recipientLabel ?? buildNodeLabel(nextHandoffNodes);
  const activityPartnerLabel = buildNodeLabel(activityPartnerNodes);
  // Only use actual next-handoff nodes — never fall back to recipientLabel,
  // which names the current holder and would produce "Toss to Bright" when
  // Bright already holds the ball.
  const nextHandoffLabel = buildNodeLabel(nextHandoffNodes);

  const tierLabels: Record<BallPossession, string> = {
    executive: 'Executive',
    middle: 'Management',
    bottom: 'Team',
  };

  return {
    userTier,
    ballIsWithMe,
    currentHolderUserId,
    iAmHolder,
    ballUnclaimed,
    canClaimBall,
    canSeeLoop,
    canSeeBelow: true,
    canActOnLoop,
    canContributeActivity,
    rootNode,
    userNode,
    tierLabel: tierLabels[userTier],
    recipientLabel,
    currentHolderLabel,
    activityPartnerLabel,
    nextHandoffLabel,
  };
}

/**
 * Turns-first possession override (ADR-catchball-consolidation).
 *
 * When a level turn exists for the loop, the ball is held by an ORG UNIT
 * (`catchball_active_branch_positions.ball_holder_org_unit_id`), not a claimed user.
 * This pure post-step re-sources the holder facts from that org unit and exposes
 * the N-tier depth, while leaving visibility (`canSeeLoop`/`canSeeBelow`) and the
 * user's own display tier (`userTier`) from the base computation intact.
 *
 * When `activePosition` is null (legacy loops with no turns yet), the base result
 * is returned unchanged — behavior stays byte-identical for those loops. Composed
 * on top of `computeOrgAwareDetails` rather than woven into it, so the pure
 * function and its unit tests stay untouched. Authority position always comes from
 * the turns view; the three-value `BallPossession` survives only as a display
 * bucket. No fifth model.
 */
export function applyActivePositionOverride(
  base: LoopAccessDetails,
  activePosition: ActiveBallPosition | null | undefined,
  orgUnitsMap: Map<string, OrgUnit>,
): LoopAccessDetails {
  if (!activePosition) return base; // legacy loop — three-tier ternary fallback, unchanged

  const holderOrgUnitId = activePosition.ballHolderOrgUnitId;
  const holderOrgUnit = orgUnitsMap.get(holderOrgUnitId) ?? null;
  const holderDepth = holderOrgUnit?.depth ?? null;
  const rootNode = base.rootNode;

  const holderTierIndex =
    holderDepth != null && rootNode ? holderDepth - rootNode.depth : null;

  // Display-only bucket, computed consistently with userTier (subtree maxDepth).
  let holderTier: BallPossession | null = null;
  if (holderDepth != null && rootNode) {
    const subtreeNodes = Array.from(orgUnitsMap.values()).filter((u) =>
      u.path?.includes(rootNode.id),
    );
    const maxDepth = subtreeNodes.reduce((max, u) => Math.max(max, u.depth), rootNode.depth);
    holderTier = deriveTierFromOrgDepth(holderDepth, rootNode.depth, maxDepth);
  }

  // Turns place the ball at an org stop; a person still Takes it before they act.
  // Personal claim alone is not enough once the turn has moved — otherwise the
  // passer still sees “You hold the ball” at the destination stop.
  const atHolderStop = base.userNode != null && base.userNode.id === holderOrgUnitId;
  const claimedUserId = base.currentHolderUserId;
  const ballUnclaimed = claimedUserId == null;
  const iAmHolder = Boolean(base.iAmHolder && atHolderStop);
  const canClaimBall = ballUnclaimed && atHolderStop;
  // In flight until someone at the destination Takes. Unclaimed, or stale
  // claim still on the passer (base.iAmHolder) while the turn has moved —
  // not when another person already holds at the destination.
  const ballAwaitingTake =
    !atHolderStop &&
    !iAmHolder &&
    Boolean(holderOrgUnitId) &&
    (ballUnclaimed || Boolean(base.iAmHolder));

  // canContributeActivity under turns = people at/adjacent to the active stop
  // (see the request, configure) even before personal claim. Act/pass stays holder-only.
  const userNodeId = base.userNode?.id ?? null;
  const userIsHolderChild =
    base.userNode != null && base.userNode.parentId === holderOrgUnitId;
  const userIsHolderParent =
    userNodeId != null && holderOrgUnit?.parentId != null && holderOrgUnit.parentId === userNodeId;
  const canContributeActivity = atHolderStop || userIsHolderChild || userIsHolderParent;

  return {
    ...base,
    activePosition,
    holderOrgUnitId,
    holderDepth,
    holderTierIndex,
    holderTier,
    currentHolderLabel: holderOrgUnit?.displayName ?? base.currentHolderLabel,
    currentHolderUserId: claimedUserId,
    iAmHolder,
    ballIsWithMe: iAmHolder,
    canActOnLoop: iAmHolder,
    ballUnclaimed,
    canClaimBall,
    canContributeActivity,
    atHolderStop,
    ballAwaitingTake,
  };
}

export function useOrgAwareCatchball(
  loop: CatchballLoop | null,
  currentUserId: string | null,
): OrgAwareCatchballResult {
  const orgUnitsMap = useEnterpriseIdentityStore((state) => state.orgUnits);
  const getUserOrgNode = useEnterpriseIdentityStore((state) => state.getUserOrgNode);
  const getUserOrgUnitIds = useEnterpriseIdentityStore((state) => state.getUserOrgUnitIds);
  const userNodeIndex = useEnterpriseIdentityStore((state) => state.userNodeIndex);
  const cycleOrgScopeNodeId = useDegapProcessStore((state) =>
    loop?.cycleId ? state.cycles.find((cycle) => cycle.id === loop.cycleId)?.orgScopeNodeId : undefined
  );
  const cycleOwnerId = useDegapProcessStore((state) =>
    loop?.cycleId ? state.cycles.find((cycle) => cycle.id === loop.cycleId)?.ownerId ?? null : null
  );
  const virtualConfig = useCatchballVirtualConfigStore((state) =>
    loop?.cycleId ? (state.configs[loop.cycleId] ?? null) : null
  );
  // Pull balls for this loop so that explicit recipient_user_id assignments
  // are honoured in the visibility/access computation even when the user has
  // no org-chart placement or virtual-config membership.
  // Stable subscription — do not call getBallsForLoop inside a Zustand selector
  // (allocates every read → infinite loop; see useBallsForLoop / §2.4).
  const ballsForLoop = useBallsForLoop(loop?.id);

  // Turns-first authority position (ADR-catchball-consolidation). Read reactively
  // from the level-turn store — a pure selector, no effect here (this hook has many
  // callers; data-arming/loadLoop lives in the workbench page). One position per
  // parallel branch; empty for loops with no turns yet → the override is a no-op
  // and the ternary path is preserved.
  const activePositions = useCatchballTierTurnStore((s) =>
    loop?.id ? s.getActivePositions(loop.id) : EMPTY_ACTIVE_POSITIONS,
  );

  return useMemo(() => {
    // Group membership (memberships table) is not mirrored into any client
    // store, so claim eligibility for group-assigned tiers stays optimistic
    // here (currentUserGroupIds omitted) — the claim RPC's check is exact.
    let details = computeOrgAwareDetails(
      loop,
      currentUserId,
      orgUnitsMap,
      userNodeIndex,
      cycleOrgScopeNodeId,
      virtualConfig,
      ballsForLoop,
      cycleOwnerId,
    );

    // Preserve the existing selector dependency, but return the shared computation.
    if (currentUserId && !details.userNode) {
      const storeUserNode = getUserOrgNode(currentUserId);
      if (storeUserNode && !details.rootNode) {
        details = { ...details, userNode: storeUserNode };
      }
    }

    // Resolve which parallel branch is relevant to this user: the branch they hold
    // if any, else the primary (first) branch. Single-ball loops have one branch.
    // Multi-placement (e.g. Lew on Mosso + Product Leads): match any assignment.
    const userPlacements = currentUserId ? getUserOrgUnitIds(currentUserId) : [];
    const placementSet = new Set(userPlacements);
    if (details.userNode?.id) placementSet.add(details.userNode.id);

    const resolvedPosition =
      activePositions.find((p) => placementSet.has(p.ballHolderOrgUnitId)) ??
      activePositions[0] ??
      null;

    // Turns-first override: stop + claim. When a level turn exists, the ball sits
    // at an org unit; eligible people at that stop Take/Grab before acting.
    let result = applyActivePositionOverride(details, resolvedPosition, orgUnitsMap);

    // Multi-placement: shallowest userNode may be Mosso while the ball sits at
    // Product Leads — move userNode to the holder stop so canClaimBall/at-stop
    // checks work. Do NOT auto-hold; pick-up is explicit.
    // Critical: also set atHolderStop. Otherwise Catchball's stale-custody
    // release effect sees claim success + atHolderStop=false and immediately
    // releaseBall — Take appears to no-op / refresh.
    const holderId = result.holderOrgUnitId ?? resolvedPosition?.ballHolderOrgUnitId ?? null;
    if (holderId && placementSet.has(holderId)) {
      const holderUnit = orgUnitsMap.get(holderId) ?? null;
      const atStop = true;
      const iAmHolder =
        !result.ballUnclaimed &&
        currentUserId != null &&
        result.currentHolderUserId === currentUserId;
      result = {
        ...result,
        userNode: holderUnit ?? result.userNode,
        atHolderStop: atStop,
        canClaimBall: result.ballUnclaimed && atStop,
        canContributeActivity: true,
        iAmHolder,
        ballIsWithMe: iAmHolder,
        canActOnLoop: iAmHolder,
        ballAwaitingTake: false,
      };
    }

    return result;
  }, [
    loop,
    currentUserId,
    orgUnitsMap,
    userNodeIndex,
    getUserOrgNode,
    getUserOrgUnitIds,
    cycleOrgScopeNodeId,
    cycleOwnerId,
    virtualConfig,
    ballsForLoop,
    activePositions,
  ]);
}

/**
 * Standalone visibility computation for loops.
 * Same logic as useOrgAwareCatchball's canSeeLoop, callable outside React (e.g. in useMemo).
 */
export function computeLoopVisibility(
  loop: CatchballLoop | null,
  currentUserId: string | null,
  orgUnitsMap: Map<string, OrgUnit>,
  userNodeIndex: Map<string, string>,
  fallbackOrgScopeNodeId?: string | null,
  virtualConfig?: CatchballVirtualConfig | null,
  ballsForLoop?: BallRecipientSlice[],
  cycleOwnerId?: string | null,
): { canSeeLoop: boolean } {
  return {
    canSeeLoop: computeOrgAwareDetails(
      loop,
      currentUserId,
      orgUnitsMap,
      userNodeIndex,
      fallbackOrgScopeNodeId,
      virtualConfig,
      ballsForLoop,
      cycleOwnerId,
    ).canSeeLoop,
  };
}

export function computeLoopAccess(
  loop: CatchballLoop | null,
  currentUserId: string | null,
  orgUnitsMap: Map<string, OrgUnit>,
  userNodeIndex: Map<string, string>,
  fallbackOrgScopeNodeId?: string | null,
  virtualConfig?: CatchballVirtualConfig | null,
  ballsForLoop?: BallRecipientSlice[],
  cycleOwnerId?: string | null,
): {
  canSeeLoop: boolean;
  canActOnLoop: boolean;
  canClaimBall: boolean;
  iAmHolder: boolean;
  ballUnclaimed: boolean;
  currentHolderUserId: string | null;
  currentHolderLabel: string | null;
} {
  const details = computeOrgAwareDetails(
    loop,
    currentUserId,
    orgUnitsMap,
    userNodeIndex,
    fallbackOrgScopeNodeId,
    virtualConfig,
    ballsForLoop,
    cycleOwnerId,
  );
  return {
    canSeeLoop: details.canSeeLoop,
    canActOnLoop: details.canActOnLoop,
    canClaimBall: details.canClaimBall,
    iAmHolder: details.iAmHolder,
    ballUnclaimed: details.ballUnclaimed,
    currentHolderUserId: details.currentHolderUserId,
    currentHolderLabel: details.currentHolderLabel,
  };
}
